import { FileTypes } from "@/pages/Private/pages/Courses/enums/FileTypes.enum";

export type PhaseMediaKind = FileTypes.Video | FileTypes.Audio | FileTypes.Image;

export interface PhaseMedia {
	kind: PhaseMediaKind;
	/** The file itself — what a player or thumbnail can be pointed at. */
	url: string;
}

const MEDIA_KINDS: ReadonlySet<FileTypes> = new Set([
	FileTypes.Video,
	FileTypes.Audio,
	FileTypes.Image,
]);

const HTTP_URL = /^https?:\/\//i;

/**
 * The first media element in a piece of HTML, with its attributes and its body. `<img>`
 * has no closing tag, so the body is allowed to run to the end of the string — only the
 * attributes matter for it anyway.
 */
const MEDIA_TAG = /<(video|audio|img)\b([^>]*)>([\s\S]*?)(?:<\/\1\s*>|$)/i;
const SRC_ATTRIBUTE = /\bsrc\s*=\s*["']([^"']+)["']/i;

const kindOfTag = (tag: string): PhaseMediaKind => {
	switch (tag.toLowerCase()) {
		case "video":
			return FileTypes.Video;
		case "audio":
			return FileTypes.Audio;
		default:
			return FileTypes.Image;
	}
};

export const isHttpUrl = (value?: string): boolean => !!value && HTTP_URL.test(value.trim());

/**
 * What media a phase's content points at, if any.
 *
 * Content is normally a bare storage URL with the content type saying what it is. But a
 * scenario imported from a crisis-exercise sheet carries its video as a complete
 * `<video controls …><source src="…" /></video>` string, and an author can type an
 * `<audio>` or `<img>` tag into a text phase by hand — the player renders both, since it
 * allows raw HTML. Anything that shows a phase at a glance has to read the URL out of
 * that markup, or it ends up printing the tag.
 *
 * Bare URLs are only media when the content type says so: a URL in a text phase is text.
 * Markup is media whatever the content type, because the tag is the author's intent.
 */
export const resolvePhaseMedia = (
	content: string | undefined | null,
	contentType?: FileTypes
): PhaseMedia | null => {
	const trimmed = content?.trim();

	if (!trimmed) {
		return null;
	}

	if (HTTP_URL.test(trimmed)) {
		return contentType && MEDIA_KINDS.has(contentType)
			? { kind: contentType as PhaseMediaKind, url: trimmed }
			: null;
	}

	const tagMatch = MEDIA_TAG.exec(trimmed);

	if (!tagMatch) {
		return null;
	}

	const [, tag, attributes, body] = tagMatch;
	// `src` on the element itself first; a `<source>` child is where the import puts it.
	const src = SRC_ATTRIBUTE.exec(attributes)?.[1] ?? SRC_ATTRIBUTE.exec(body)?.[1];

	if (!src?.trim()) {
		return null;
	}

	return { kind: kindOfTag(tag), url: src.trim() };
};

/** The URL to play or show for a phase's content: the resolved media, else the content as is. */
export const phaseMediaUrl = (
	content: string | undefined | null,
	contentType?: FileTypes
): string => resolvePhaseMedia(content, contentType)?.url ?? content ?? "";

/** The last path segment of a media URL, decoded for display. */
export const mediaFileName = (url: string): string => {
	const last = url.split("/").pop() ?? "";

	try {
		return decodeURIComponent(last);
	} catch {
		return last;
	}
};
